import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShoppingCart,
  faTimes,
  faMinus,
  faPlus,
  faTrash,
  faArrowRight,
  faShoppingBag
} from '@fortawesome/free-solid-svg-icons';
import { useBasket } from '../../contexts/BasketContext';
import { theme } from '../../styles/theme';

const SidebarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -5px 0 25px rgba(0, 0, 0, 0.1);
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f0f0f0;
    color: ${props => props.theme.colors.primary};
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const BasketItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 1rem;
`;

const ItemImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  background: #f8f9fa;
  flex-shrink: 0;
`;

const ItemImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemName = styled.h3`
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  line-height: 1.3;
`;

const ItemPrice = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
`;

const ItemControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const QuantityButton = styled.button`
  width: 30px;
  height: 30px;
  border: 1px solid #e0e0e0;
  background: white;
  color: ${props => props.theme.colors.primary};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
    border-color: ${props => props.theme.colors.accent};
  }
  
  &:disabled {
    background: #f0f0f0;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 40px;
  height: 30px;
  border: 1px solid #e0e0e0;
  text-align: center;
  font-size: 0.9rem;
  border-radius: 5px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ff4444;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #ff4444;
    color: white;
  }
`;

const EmptyBasket = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  color: #ccc;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  font-size: 1.1rem;
  margin: 0;
`;

const SidebarFooter = styled.div`
  padding: 2rem;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const TotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const TotalLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const TotalPrice = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.accent}, #d4a574);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(157, 109, 73, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    background: linear-gradient(45deg, #8a5d3a, #c19a6b);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(157, 109, 73, 0.4);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ClearButton = styled.button`
  width: 100%;
  padding: 0.8rem;
  background: white;
  border: 2px solid #ff4444;
  color: #ff4444;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background: #ff4444;
    color: white;
  }
`;

const BasketSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { 
    items, 
    totalItems, 
    totalPrice, 
    updateQuantity, 
    removeFromBasket, 
    clearBasket 
  } = useBasket();

  const handleQuantityChange = (itemId, change) => {
    const item = items.find(item => item.id === itemId);
    if (item) {
      const limit = item.manage_stock && Number.isFinite(item.stock_quantity)
        ? Math.max(item.stock_quantity, 0)
        : Infinity;
      const desired = item.quantity + change;
      if (desired <= 0) {
        return;
      }
      const clamped = limit === Infinity ? desired : Math.min(desired, limit);
      updateQuantity(itemId, clamped);
    }
  };

  const handleQuantityInput = (itemId, value) => {
    const item = items.find(item => item.id === itemId);
    if (item) {
      const limit = item.manage_stock && Number.isFinite(item.stock_quantity)
        ? Math.max(item.stock_quantity, 0)
        : Infinity;
      const quantity = parseInt(value, 10);
      if (quantity > 0) {
        const clamped = limit === Infinity ? quantity : Math.min(quantity, limit);
        updateQuantity(itemId, clamped);
      }
    }
  };

  const handleCheckout = () => {
    onClose();
    const path = window.location.pathname || '';
    if (path.startsWith('/farm-shop') || path.includes('/retail-village/shops/farm-shop')) {
      navigate('/farm-shop/checkout');
    } else {
      navigate('/duchess-collection/checkout');
    }
  };

  return (
    <>
      <SidebarOverlay isOpen={isOpen} onClick={onClose} />
      <SidebarContainer isOpen={isOpen}>
        <SidebarHeader>
          <SidebarTitle>
            <FontAwesomeIcon icon={faShoppingCart} />
            Basket ({totalItems})
          </SidebarTitle>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </SidebarHeader>

        <SidebarContent>
          {items.length === 0 ? (
            <EmptyBasket>
              <EmptyIcon>
                <FontAwesomeIcon icon={faShoppingBag} />
              </EmptyIcon>
              <EmptyText>Your basket is empty</EmptyText>
            </EmptyBasket>
          ) : (
            items.map(item => {
              const isManaged = item.manage_stock && Number.isFinite(item.stock_quantity);
              const stockLimit = isManaged ? Math.max(item.stock_quantity, 0) : Infinity;
              const disableMinus = item.quantity <= 1;
              const disablePlus = isManaged ? item.quantity >= stockLimit : item.quantity >= 10;

              return (
              <BasketItem key={item.id}>
                <ItemImage>
                  <ItemImg
                    src={item.image}
                    alt={item.name}
                    onError={(e) => {
                      e.target.src = '/placeholder-product.jpg';
                    }}
                  />
                </ItemImage>
                
                <ItemDetails>
                  <ItemName>{item.name}</ItemName>
                  <ItemPrice>£{item.price.toFixed(2)}</ItemPrice>
                  
                  <ItemControls>
                    <QuantityButton
                      onClick={() => handleQuantityChange(item.id, -1)}
                      disabled={disableMinus}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </QuantityButton>
                    
                    <QuantityInput
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleQuantityInput(item.id, e.target.value)}
                      min="1"
                      max={stockLimit === Infinity ? undefined : stockLimit}
                    />
                    
                    <QuantityButton
                      onClick={() => handleQuantityChange(item.id, 1)}
                      disabled={disablePlus}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </QuantityButton>
                    
                    <RemoveButton
                      onClick={() => removeFromBasket(item.id)}
                      title="Remove item"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </RemoveButton>
                  </ItemControls>
                </ItemDetails>
              </BasketItem>
            );})
          )}
        </SidebarContent>

        {items.length > 0 && (
          <SidebarFooter>
            <TotalSection>
              <TotalLabel>Total ({totalItems} items)</TotalLabel>
              <TotalPrice>£{totalPrice.toFixed(2)}</TotalPrice>
            </TotalSection>
            
            <CheckoutButton onClick={handleCheckout}>
              Proceed to Checkout
              <FontAwesomeIcon icon={faArrowRight} />
            </CheckoutButton>
            
            <ClearButton onClick={clearBasket}>
              Clear Basket
            </ClearButton>
          </SidebarFooter>
        )}
      </SidebarContainer>
    </>
  );
};

export default BasketSidebar;